import { type SiteId } from '@solinkcorp/api-types';
import { type DatePickerLocale, enUS, es, fr } from '@solinkcorp/solink-ui/components/DatePicker';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import moment from 'moment';
import { LocationStore } from 'stores/LocationStore';

export function resolveTimezone(input: {
  playingLocationId: SiteId | undefined;
  groupLocationId: SiteId | undefined;
  selectedLocationIds: readonly SiteId[];
  timezoneIdOverride: string;
}): string {
  if (input.selectedLocationIds.length > 1) {
    return input.timezoneIdOverride;
  }

  let selectedLocationId: SiteId = ALL_LOCATIONS as SiteId;
  if (input.playingLocationId) {
    selectedLocationId = input.playingLocationId;
  } else if (input.groupLocationId) {
    selectedLocationId = input.groupLocationId;
  } else if (input.selectedLocationIds.length === 1) {
    selectedLocationId = input.selectedLocationIds[0];
  }

  const location = LocationStore.getLocation(selectedLocationId);
  return location?.timezone ?? getLocalTimeZone();
}

/**
 * Left month of the two-month calendar.
 * When `onRight` is true, `focus` sits in the right month.
 * The result is never earlier than the month of `earliest`.
 */
export function leftCalendarMonth(focus: Date, earliest: Date | undefined, onRight: boolean): Date {
  const focusMonth = new Date(focus.getFullYear(), focus.getMonth(), 1);
  const left = onRight
    ? new Date(focusMonth.getFullYear(), focusMonth.getMonth() - 1, 1)
    : focusMonth;
  if (!earliest) {
    return left;
  }
  const earliestMonth = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
  return left.getTime() < earliestMonth.getTime() ? earliestMonth : left;
}

/** Local calendar day whose Y/M/D matches `instant` in `timeZone`. DayPicker selects local days. */
export function calendarDay(instant: Date | number, timeZone: string): Date {
  const zoned = moment.tz(instant, timeZone);
  return new Date(zoned.year(), zoned.month(), zoned.date());
}

/**
 * The picked day is a local Date. Read its Y/M/D as a day in `timeZone`.
 * Keep the current time of day when the header shows a time, so playback does not jump to midnight.
 */
export function instantOnCalendarDay(
  day: Date,
  timeZone: string,
  timeSource: Date | number | undefined,
): Date {
  const source = timeSource === undefined ? undefined : moment.tz(timeSource, timeZone);
  return moment
    .tz(
      {
        year: day.getFullYear(),
        month: day.getMonth(),
        date: day.getDate(),
        hour: source?.hour() ?? 0,
        minute: source?.minute() ?? 0,
        second: source?.second() ?? 0,
      },
      timeZone,
    )
    .toDate();
}

/** Same calendar day as `instant`, with the clock set to `time`. Milliseconds go to 0. */
export function withTimeOfDay(
  instant: number,
  timeZone: string,
  time: { hour: number; minute: number; second?: number },
): number {
  return moment
    .tz(instant, timeZone)
    .hour(time.hour)
    .minute(time.minute)
    .second(time.second ?? 0)
    .millisecond(0)
    .valueOf();
}

/** `HH:mm:ss` for `<input type="time" step="1">`. The value is always 24-hour. */
export function formatTimeInputValue(instant: number, timeZone: string): string {
  return moment.tz(instant, timeZone).format('HH:mm:ss');
}

const TIME_INPUT_VALUE = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

/** The same calendar day as `instant`, with the clock from an `<input type="time">` value. */
export function instantWithTimeInput(
  instant: number,
  timeZone: string,
  value: string,
): number | undefined {
  const match = TIME_INPUT_VALUE.exec(value);
  if (!match) {
    return;
  }
  return withTimeOfDay(instant, timeZone, {
    hour: Number(match[1]),
    minute: Number(match[2]),
    second: match[3] === undefined ? 0 : Number(match[3]),
  });
}

export interface InstantBounds {
  earliest?: number;
  latest?: number;
}

/**
 * Guest limits, with `now` as a ceiling.
 * A future instant is not selectable: storing it would play the current time instead.
 */
export function selectableInstantBounds(
  minDate: number | undefined,
  maxDate: number | undefined,
  now: number,
): InstantBounds {
  return {
    earliest: minDate,
    latest: maxDate === undefined ? now : Math.min(maxDate, now),
  };
}

export type TimeInputError = 'future' | 'tooEarly' | 'tooLate';

/**
 * A typed time outside `bounds`.
 * `bounds.latest` is min(maxDate, now). A time after a guest cutoff is too late
 * when that cutoff is earlier than now. It is in the future only when it is after now.
 */
export function timeInputError(
  typedTime: number | undefined,
  bounds: InstantBounds,
  now: number,
): TimeInputError | undefined {
  if (typedTime === undefined || clampInstant(typedTime, bounds) === typedTime) {
    return;
  }
  if (typedTime > now) {
    return 'future';
  }
  if (bounds.latest !== undefined && typedTime > bounds.latest) {
    return 'tooLate';
  }
  return 'tooEarly';
}

/**
 * Close writes a draft that is not already stored.
 * Playback moves the store date after a save. A second write replaces that progress.
 * A live clock keeps moving. Close leaves that clock alone.
 */
export function draftNeedsCommitOnClose(
  draft: { instant: number; unit: string; live: boolean },
  committed: { instant: number; unit: string; live: boolean },
): boolean {
  if (draft.live !== committed.live || draft.unit !== committed.unit) {
    return true;
  }
  if (draft.live) {
    return false;
  }
  return draft.instant !== committed.instant;
}

/** Pull `instant` into `bounds`. A future earliest does not move the instant past `latest`. */
export function clampInstant(instant: number, bounds: InstantBounds): number {
  const { earliest, latest } = bounds;
  if (latest !== undefined && earliest !== undefined && earliest > latest) {
    return instant > latest ? latest : instant;
  }
  let next = instant;
  if (earliest !== undefined && next < earliest) {
    next = earliest;
  }
  if (latest !== undefined && next > latest) {
    next = latest;
  }
  return next;
}

function isSamePickerDay(a: number, b: number, timeZone: string): boolean {
  const left = calendarDay(a, timeZone);
  const right = calendarDay(b, timeZone);
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

/**
 * `min` and `max` for the time input on the calendar day of `instant`.
 * A bound on another day does not apply.
 */
export function timeInputBounds(
  instant: number,
  timeZone: string,
  bounds: InstantBounds,
): { min?: string; max?: string } {
  const min =
    bounds.earliest !== undefined && isSamePickerDay(instant, bounds.earliest, timeZone)
      ? formatTimeInputValue(bounds.earliest, timeZone)
      : undefined;
  const max =
    bounds.latest !== undefined && isSamePickerDay(instant, bounds.latest, timeZone)
      ? formatTimeInputValue(bounds.latest, timeZone)
      : undefined;
  if (min !== undefined && max !== undefined && min > max) {
    return {};
  }
  return { min, max };
}

/** Locale clock string with seconds, for example "2:05:00 PM" or "14:05:00". */
export function formatClockTime(instant: number, timeZone: string): string {
  return moment.tz(instant, timeZone).format('LTS');
}

/** Short zone name on `instant`, for example "EST". */
export function formatTimezone(instant: number, timeZone: string): string {
  return moment.tz(instant, timeZone).format('z');
}

function bareToken(token: string): string {
  return token.replace(/[.,]+$/g, '').toLowerCase();
}

function nameSet(names: string[]): Set<string> {
  return new Set(names.map((name) => bareToken(name)));
}

/** Drop a leading weekday token. "Sun Aug 16" becomes "Aug 16". "Wed, 19 Aug" becomes "19 Aug". */
function stripWeekdayPrefix(segment: string): string {
  const match = segment.match(/^(\S+)\s+([\s\S]+)$/);
  if (!match) {
    return segment;
  }
  const token = bareToken(match[1]);
  const rest = match[2].replace(/^[,\s]+/, '');
  if (!token || !nameSet([...moment.weekdaysShort(), ...moment.weekdays()]).has(token)) {
    return segment;
  }
  // "mar." is both Tuesday and March. "mar. 2026" is a month, so the year stays with it.
  const months = nameSet([...moment.monthsShort(), ...moment.months()]);
  if (months.has(token) && /^\d{4}\b/.test(rest)) {
    return segment;
  }
  return rest;
}

/** "mer., 19 août" becomes "19 août" when the active moment locale has no "mer". */
function dropShortWeekdayComma(segment: string): string {
  const comma = segment.indexOf(',');
  if (comma === -1 || comma > 6) {
    return segment;
  }
  return segment.slice(comma + 1).trim();
}

/**
 * Drop weekday names from a picker label.
 * "Wed, 19 Aug" becomes "19 Aug". "Sun Aug 16 - Sat Aug 22" becomes "Aug 16 - Aug 22".
 */
export function labelWithoutWeekday(dateLabel: string): string {
  return dateLabel
    .split(' - ')
    .map((part) => dropShortWeekdayComma(stripWeekdayPrefix(part)))
    .join(' - ');
}

/** Sidebar button label. Clock time is included when the picker edits a time of day. The zone stays in the popover. */
export function formatPickerLabel(
  dateLabel: string,
  instant: number,
  timeZone: string,
  showTime: boolean,
): string {
  if (!showTime) {
    return dateLabel;
  }
  return `${dateLabel} ${formatClockTime(instant, timeZone)}`;
}

/** True when the active moment locale prints a meridiem in its short time format. */
export function clockUses12Hour(): boolean {
  return /a/i.test(moment.localeData().longDateFormat('LT'));
}

/**
 * `lang` for a native time input.
 * The control paints 12-hour or 24-hour from this value. It does not read the app locale.
 * `en-US` is 12-hour. `en-GB` is 24-hour.
 */
export function timeInputLang(): string {
  return clockUses12Hour() ? 'en-US' : 'en-GB';
}

export function datePickerLocale(localeCode: string): DatePickerLocale {
  if (localeCode.startsWith('fr')) {
    return fr;
  }
  if (localeCode.startsWith('es')) {
    return es;
  }
  return enUS;
}
